import React, { useState, useEffect } from "react";

import Select from "react-select";
import Tippy from "@tippyjs/react";
import { isEmpty } from "lodash";

import LookupsAPI from "../../API/lookups.api";
import LoggersAPI from "../../API/loggers.api";

import LoadingOverlay from "../../components/Loaders/LoadingOverlay";
import CardTable from "../../components/Tables/CardTable";
import Pagination from "../../components/Pagination/Pagination";
import FilterDropdown from "../../components/Dropdown/FilterDropdown";
import { getCurrentDateYMD } from "../../utils/DateHelper";

import { ErrorAlert } from "../../utils/Alerts";
import {
    HTTP_METHODS,
    ACTION_TYPE,
    ENTITY_TYPE,
    BOOLEAN_OPTIONS,
} from "../../utils/constants";
import { shortDateTime } from "../../utils/DateHelper";
import ActivityDetailsModal from "../../components/Modals/ActivityDetailsModal";
import { Trans, useTranslation } from "react-i18next";
import { getMappedFilters } from "../../utils/FilterHelpers";
import { getSessionFilters } from "../../utils/FilterHelpers";
import { setObject, getObject } from "../../utils/StorageHelpers";
import LogCapture from "../../utils/LogManagement/LogHelper";
import { getReactSelectTheme } from "../../utils/ReactSelectStyles";
import { handleExternalLink } from "../../utils/ExternalLinksHelper";
import { toCamelCase } from "../../utils/Helpers";
import { t } from "i18next";
import MissingData from "../../components/Shared/MissingData";

const headers = [
    { label: "#", name: "number", skipSort: true },
    { label: t("createdAt"), name: "createdAt" },
    { label: t("method"), name: "method", skipSort: true },
    { label: t("path"), name: "path", skipSort: true },
    { label: t("logMessage"), name: "logMessage", skipSort: true },
    { label: t("isError"), name: "isError", skipSort: true },
    { label: t("actionType"), name: "actionType", skipSort: true },
    { label: t("entityType"), name: "entityType", skipSort: true },
];

const ActivityFeed = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [sortBy, setSortBy] = useState("createdAt");
    const [sortDirection, setSortDirection] = useState("DESC");
    const [currentPage, setCurrentPage] = useState(1);
    const [isRendered, setIsRendered] = useState(false);

    const [users, setUsers] = useState([]);
    const [selectedUser, setSelectedUser] = useState(() =>
        getSessionFilters("Activity-Feed-user")
    );

    const [logs, setLogs] = useState([]);
    const [logsCount, setLogsCount] = useState(0);

    const [filters, setFilters] = useState({});
    const [appliedFilters, setAppliedFilters] = useState(() =>
        getSessionFilters("Activity-Feed-filters")
    );
    const [filtersUsed, setFiltersUsed] = useState(false);

    const [detailsModal, setDetailsModal] = useState(false);
    const [clickedRow, setClickedRow] = useState({});

    const [isExporting, setIsExporting] = useState(false);

    const { t } = useTranslation();

    const [itemsPerRequest, setItemsPerRequest] = useState(
        () => getObject("Activity-Feed-itemsPerPage", true) || 10
    );

    const keys = [
        {
            name: "number",
            element: (row, index) => {
                return (
                    <span className="whitespace-nowrap content-center">
                        {index + 1 + (currentPage - 1) * itemsPerRequest}
                    </span>
                );
            },
        },
        {
            name: "createdAt",
            element: (row) => {
                return (
                    <span className="whitespace-nowrap content-center">
                        {shortDateTime(row.createdAt)}
                    </span>
                );
            },
        },
        { name: "method" },
        {
            name: "path",
            element: (row) => {
                return row.path ? (
                    <Tippy
                        content={row.path}
                        placement="top"
                        animation="scale"
                        className="break-words"
                    >
                        <span
                            tabIndex="1"
                            className="w-56 tooltip-container break-words"
                        >
                            {row.path}
                        </span>
                    </Tippy>
                ) : (
                    "---"
                );
            },
        },
        {
            name: "logMessage",
            element: (row) => {
                return row.logMessage ? (
                    <Tippy
                        content={row.logMessage}
                        placement="top"
                        animation="scale"
                        className="break-words"
                    >
                        <span tabIndex="2" className="w-56 tooltip-container">
                            {row.logMessage}
                        </span>
                    </Tippy>
                ) : (
                    "---"
                );
            },
        },
        {
            name: "isError",
            element: (row) => {
                return (
                    <span
                        className={`px-2 tag-ele ${
                            row.isError ? "tag-declined" : "tag-completed"
                        }`}
                    >
                        {row.isError ? t("failure") : t("success")}
                    </span>
                );
            },
        },
        { name: "actionType" },
        { name: "entityType" },
    ];

    const searchFilters = [
        {
            label: t("isError"),
            name: "isError",
            type: "Dropdown",
            options: Object.values(BOOLEAN_OPTIONS).map((val) => ({
                label: t(val),
                value: val,
            })),
        },
        {
            label: t("path"),
            name: "path",
            type: "Input",
            placeholder: t("pathPlaceholder"),
        },
        {
            label: t("method"),
            name: "method",
            type: "Dropdown",
            options: Object.keys(HTTP_METHODS).map((key) => {
                return { label: HTTP_METHODS[key], value: key };
            }),
        },
        {
            label: t("actionType"),
            name: "actionType",
            type: "Dropdown",
            options: Object.keys(ACTION_TYPE).map((key) => {
                return { label: ACTION_TYPE[key], value: key };
            }),
        },
        {
            label: t("entityType"),
            name: "entityType",
            type: "Dropdown",
            options: Object.keys(ENTITY_TYPE).map((key) => {
                return { label: t(toCamelCase(ENTITY_TYPE[key])), value: key };
            }),
        },
        {
            label: t("createdAt"),
            name: "createdAt",
            type: "Date",
            max: getCurrentDateYMD(),
        },
    ];

    const detailsInputFields = [
        { label: t("isError"), name: "isError", type: "text" },
        { label: t("createdAt"), name: "createdAt", type: "datetime" },
        { label: t("entityId"), name: "entityId", type: "text" },
        { label: t("path"), name: "path", type: "text" },
        { label: t("method"), name: "method", type: "text" },
        { label: t("entityType"), name: "entityType", type: "text" },
        { label: t("actionType"), name: "actionType", type: "text" },
    ];

    const detailsTextFields = [
        { label: t("logMessage"), name: "logMessage" },
        { label: t("userDetails"), name: "user" },
        { label: t("beforeAction"), name: "beforeAction" },
        { label: t("afterAction"), name: "afterAction" },
        { label: t("payload"), name: "payload" },
    ];

    useEffect(() => {
        fetchUsers();
    }, []);

    useEffect(() => {
        if (isRendered) {
            if (sortBy && sortDirection && !isEmpty(selectedUser)) {
                setCurrentPage(1);
                fetchLogs(selectedUser, appliedFilters, sortDirection);
            } else if (!isEmpty(selectedUser)) {
                setCurrentPage(1);
                fetchLogs(selectedUser, appliedFilters);
            }
        }
    }, [sortBy, sortDirection]);

    useEffect(() => {
        if (!isEmpty(selectedUser)) {
            fetchLogs(selectedUser, appliedFilters, sortDirection);
        }
        setIsRendered(true);
    }, [selectedUser]);

    const fetchUsers = async () => {
        try {
            const result = await LookupsAPI.getSupervisedUsers(true);
            const mappedResult = result.map((user) => {
                return {
                    userEmail: user?.email,
                    label: (
                        <span className="flex items-center gap-1">
                            {user?.email}
                            <span className="text-xxs text-primary-color-orange">
                                ({user?.organization?.name})
                            </span>
                        </span>
                    ),
                    value: {
                        userId: user?.id,
                        organizationId: user?.organization?.id,
                    },
                };
            });
            setUsers(mappedResult);
        } catch (error) {
            ErrorAlert(error?.message || error);
        }
    };

    const fetchLogs = async (
        _user,
        filters,
        _sortDirection = "DESC",
        offset = 0,
        itemsPerPage = itemsPerRequest
    ) => {
        setIsLoading(true);
        const newFilter = getMappedFilters(filters, ["isError"]);
        const result = await LoggersAPI.listLoggers(
            _user?.userId,
            _user?.organizationId,
            itemsPerPage,
            newFilter,
            offset,
            _sortDirection
        );

        setLogs(result?.data || []);
        setLogsCount(result?.totalCount || 0);
        setIsLoading(false);
    };

    const handleUserSelection = (_user) => {
        setSelectedUser(_user.value);
        setObject("Activity-Feed-user", _user.value, true);
    };

    const handlePageClick = (index) => {
        if (index !== currentPage) {
            setCurrentPage(index);
            fetchLogs(selectedUser, appliedFilters, sortDirection, index - 1);
        }
    };

    const handleItemsPerPage = (itemsPerPage) => {
        setItemsPerRequest(itemsPerPage);
        setCurrentPage(1);
        setObject("Activity-Feed-itemsPerPage", itemsPerPage, true);
        // call API to update the items per page
        fetchLogs(selectedUser, appliedFilters, sortDirection, 0, itemsPerPage);
    };

    const applyFilters = (specificAppliedFilters) => {
        if (!isEmpty(specificAppliedFilters)) {
            setFiltersUsed(true);
        }
        fetchLogs(selectedUser, specificAppliedFilters, sortDirection, 0);
    };

    const exportFile = async () => {
        try {
            setIsExporting(true);
            const result = await LoggersAPI.exportLoggers(
                selectedUser?.userId,
                appliedFilters
            );
            if (!isEmpty(result)) {
                LogCapture.info(
                    `Logs exported for ${getSelectedUserObject()?.userEmail}`
                );
                handleExternalLink(result);
            }
        } catch (error) {
            ErrorAlert(error?.message || error);
            LogCapture.error(
                `Couldn't export logs for ${
                    getSelectedUserObject()?.userEmail
                }, error: ${error?.message || error}`
            );
        } finally {
            setIsExporting(false);
        }
    };

    const onRowClickCallback = (row) => {
        setDetailsModal(true);
        setClickedRow(row);
    };

    const getSelectedUserObject = () => {
        return users?.find(
            (user) =>
                user.value?.userId === selectedUser?.userId &&
                user.value?.organizationId === selectedUser?.organizationId
        );
    };

    return (
        <>
            <header className="main-content-header flex items-center justify-between">
                <h1 className="h1">
                    <Trans i18nKey="activityFeed">Activity Feed</Trans>
                </h1>
                <div className="action-header-wrapper flex items-center">
                    <FilterDropdown
                        filterOptions={searchFilters}
                        filters={filters}
                        setFilters={setFilters}
                        applyFilters={applyFilters}
                        component="Activity-Feed"
                        appliedFilters={appliedFilters}
                        setAppliedFilters={setAppliedFilters}
                        disabled={isEmpty(selectedUser)}
                    />
                    <button
                        onClick={exportFile}
                        disabled={isEmpty(selectedUser) || isExporting}
                        className={`default-orange-outline-btn items-center ml-1 h-7 ${
                            (isEmpty(selectedUser) || isExporting) && "disabled"
                        }`}
                    >
                        <div className="flex items-center">
                            {isExporting ? (
                                <>
                                    <span className="animate-spin mr-3 inline-flex">
                                        <i className="fas fa-sync"></i>
                                    </span>
                                    <Trans i18nKey="exporting">
                                        Exporting...
                                    </Trans>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-file-download" />
                                    <span className="ml-2">
                                        <Trans i18nKey="export">Export</Trans>
                                    </span>
                                </>
                            )}
                        </div>
                    </button>
                </div>
            </header>
            <LoadingOverlay active={isLoading}>
                <div className="card-table shadow">
                    <div className="flex items-center mb-4">
                        <p className="whitespace-nowrap text-base">
                            <Trans i18nKey="selectUser">Select User</Trans>
                        </p>
                        <Select
                            onChange={handleUserSelection}
                            className="ml-3 w-full"
                            isSearchable={true}
                            options={users}
                            defaultValue={getSelectedUserObject()}
                            placeholder={t("selectPlaceholder", {
                                entity: "User",
                            })}
                            theme={getReactSelectTheme}
                            getOptionValue={(option) =>
                                `${option.userEmail} - ${option.value?.organizationId}`
                            }
                        />
                        <button
                            className="secondary-green-btn flex justify-center items-center ml-2 px-4"
                            onClick={() => {
                                fetchLogs(
                                    selectedUser,
                                    appliedFilters,
                                    sortDirection
                                );
                                setCurrentPage(1);
                            }}
                            disabled={isEmpty(selectedUser) || isLoading}
                        >
                            <i className="fas fa-redo mr-2" />
                            <Trans i18nKey="refresh">Refresh</Trans>
                        </button>
                    </div>

                    {logs.length > 0 ? (
                        <>
                            <CardTable
                                keys={keys}
                                headers={headers}
                                rows={logs}
                                purpose={"Activity-Feed"}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                                ignoreFirstChild={true}
                                onRowClickCallback={onRowClickCallback}
                            />
                            <Pagination
                                currentPage={currentPage}
                                setCurrentPage={handlePageClick}
                                totalCount={logsCount}
                                sizePerPage={itemsPerRequest}
                                handleItemsPerPage={handleItemsPerPage}
                            />
                        </>
                    ) : (
                        <MissingData
                            isFilterUsed={filtersUsed}
                            entity={t("activityFeed")}
                        />
                    )}
                </div>
            </LoadingOverlay>
            <ActivityDetailsModal
                isVisible={detailsModal}
                setIsVisible={setDetailsModal}
                inputFields={detailsInputFields}
                textFields={detailsTextFields}
                data={clickedRow}
            />
        </>
    );
};

export default ActivityFeed;
